import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import { MapPin, AlertTriangle } from "lucide-react";

/**
 * PinConfirmModal — one-tap "does this look right?" screen shown between
 * "Pay securely" and Razorpay. Prevents the classic wrong-pin support ticket.
 *
 * If lat/lng is present, shows a locked mini-map with the drop pin.
 * If not, shows a friendly warning inviting the customer to set a pin.
 */
export default function PinConfirmModal({
  open, onEdit, onConfirm, customer, apiKey, total, submitting,
}) {
  if (!open) return null;
  const hasPin = typeof customer.lat === "number" && typeof customer.lng === "number";
  const addressLines = [
    customer.full_name,
    [customer.address_line1, customer.address_line2].filter(Boolean).join(", "),
    [customer.area, customer.landmark].filter(Boolean).join(" · "),
    [customer.city, customer.state, customer.pincode].filter(Boolean).join(" · "),
    customer.mobile,
  ].filter(Boolean);

  return (
    <div
      data-testid="pin-confirm-modal"
      className="fixed inset-0 z-[80] bg-espresso/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onEdit(); }}
    >
      <div className="bg-ivory w-full sm:max-w-lg border border-line max-h-[92vh] overflow-y-auto">
        <div className="p-5 sm:p-7">
          <div className="label-xs text-espresso-light">Confirm your address</div>
          <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-espresso leading-tight">
            Is this the right drop?
          </h2>
          <p className="mt-2 text-sm text-espresso-light">
            One quick check — the rider will follow this exact pin.
          </p>

          {hasPin && apiKey ? (
            <div className="mt-5 border border-line" style={{ height: 220 }}>
              <APIProvider apiKey={apiKey}>
                <Map
                  defaultCenter={{ lat: customer.lat, lng: customer.lng }}
                  defaultZoom={17}
                  mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
                  disableDefaultUI={true}
                  gestureHandling="none"
                  zoomControl={false}
                  style={{ width: "100%", height: "100%" }}
                >
                  <AdvancedMarker position={{ lat: customer.lat, lng: customer.lng }}>
                    <MapPin className="h-9 w-9 text-espresso drop-shadow-lg" fill="#C9A44E" strokeWidth={1.8} />
                  </AdvancedMarker>
                </Map>
              </APIProvider>
            </div>
          ) : (
            <div className="mt-5 bg-amber-50 border-l-2 border-amber-500 pl-4 py-3 flex items-start gap-2" data-testid="pin-missing-warning">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm text-espresso font-medium">No exact pin set</div>
                <div className="text-xs text-espresso-light mt-1">
                  Riders may struggle to find you. Consider going back and dropping a pin on the map for a smoother delivery.
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 border border-line/60 bg-ivory-100/40 p-4 text-sm text-espresso" data-testid="pin-confirm-address">
            {addressLines.map((line, i) => (
              <div key={i} className={i === 0 ? "font-medium" : "text-espresso-light text-xs mt-0.5"}>
                {line}
              </div>
            ))}
            {customer.instructions && (
              <div className="mt-3 pt-3 border-t border-line/50 text-xs italic text-espresso-light">
                Note: {customer.instructions}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
            <button
              type="button"
              onClick={onEdit}
              disabled={submitting}
              data-testid="pin-confirm-edit"
              className="btn-outline flex-1 disabled:opacity-40"
            >
              Edit address
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={submitting}
              data-testid="pin-confirm-pay"
              className="btn-espresso flex-1 disabled:opacity-40"
            >
              {submitting ? "Placing…" : `Confirm & pay ₹${(total || 0).toFixed(0)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
